import { addNamespace } from './utils'

/**
 * @file
 * @author Thomas Grinderslev <thomas@edulab.dk>
 */

/**
 * @typedef {{focusedInputField: Vue}} KeyboardStoreState
 */

/**
 * The types used in this store
 * @enum {string}
 */
export const KeyboardStoreTypes = {
  getters: {
    GET_FOCUSED_INPUT_FIELD: 'getFocusedInputField',
    IS_KEYPRESS_IN_PROGRESS: 'isKeypressInProgress',
    IS_KEYBOARD_VISIBLE: 'isKeyboardVisible',
  },
  actions: {
    CLEAR_FOCUS: 'clearFocus',
    SET_FOCUS: 'setFocus',
    SET_KEYPRESS_IN_PROGRESS: 'setKeypressInProgress',
  },
  mutations: {
    CLEAR_FOCUS: 'clearFocus',
    SET_FOCUS: 'setFocus',
    SET_KEYPRESS_IN_PROGRESS: 'setKeypressInProgress',
  },
}

/**
 * A namespaced version of the types used in this store
 * @enum {string}
 */
export const KeyboardStoreNamespacedTypes = addNamespace(
  'keyboard',
  KeyboardStoreTypes
)

export function state() {
  return { focusedInputField: null, keypressInProgress: false }
}

export const getters = {
  /**
   * @param {KeyboardStoreState} state
   * @return {function(*): boolean}
   */
  [KeyboardStoreTypes.getters.GET_FOCUSED_INPUT_FIELD]: state => () => {
    return state.focusedInputField
  },

  /**
   * @param {KeyboardStoreState} state
   * @return {function(*): boolean}
   */
  [KeyboardStoreTypes.getters.IS_KEYPRESS_IN_PROGRESS]: state => () => {
    return state.keypressInProgress
  },

  /**
   * @param {KeyboardStoreState} state
   * @return {function(*): boolean}
   */
  [KeyboardStoreTypes.getters.IS_KEYBOARD_VISIBLE]: state => () => {
    return !!state.focusedInputField
  },
}

export const actions = {
  /**
   * @param {VuexCommit} commit
   * @param {KeyboardStoreState} state
   * @param {Vue} inputField
   */
  [KeyboardStoreTypes.actions.CLEAR_FOCUS]({ commit, state }, inputField) {
    if (!inputField || state.focusedInputField === inputField) {
      commit(KeyboardStoreTypes.mutations.CLEAR_FOCUS)
    }
  },

  /**
   * @param {VuexCommit} commit
   * @param {Vue} inputField
   */
  [KeyboardStoreTypes.actions.SET_FOCUS]({ commit }, inputField) {
    commit(KeyboardStoreTypes.mutations.SET_FOCUS, inputField)
  },

  /**
   * @param {VuexCommit} commit
   * @param {boolean} keypressInProgress
   */
  [KeyboardStoreTypes.actions.SET_KEYPRESS_IN_PROGRESS](
    { commit },
    keypressInProgress
  ) {
    commit(
      KeyboardStoreTypes.mutations.SET_KEYPRESS_IN_PROGRESS,
      keypressInProgress
    )
  },
}

export const mutations = {
  /**
   * @param {KeyboardStoreState} state
   */
  [KeyboardStoreTypes.mutations.CLEAR_FOCUS](state) {
    state.focusedInputField = null
  },

  /**
   * @param {KeyboardStoreState} state
   * @param {Vue} inputField
   */
  [KeyboardStoreTypes.mutations.SET_FOCUS](state, inputField) {
    state.focusedInputField = inputField
  },

  /**
   * @param {KeyboardStoreState} state
   * @param {boolean} keypressInProgress
   */
  [KeyboardStoreTypes.mutations.SET_KEYPRESS_IN_PROGRESS](
    state,
    keypressInProgress
  ) {
    state.keypressInProgress = keypressInProgress
  },
}

export default {
  namespaced: true,
  Types: KeyboardStoreTypes,
  NamespacedTypes: KeyboardStoreNamespacedTypes,
  state,
  getters,
  actions,
  mutations,
}
